import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Link,
  Menu,
  MenuButton,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { FiEdit, FiTrash2, FiClock } from "react-icons/fi";
import { useNavigate, useParams, useSearchParams, Link as RouterLink } from "react-router-dom";
import { SingleCustomerBlock } from "../../components/CustomerBlock/SingleCustomerBlock/SingleCustomerBlock";
import { HistoryCustomerBlock } from "../../components/CustomerBlock/SingleCustomerBlock/HistoryCustomerBlock";
import { Layout } from "../../components/Layout/Layout";
import { useAppDispatch, useAppSelector } from "../../store";
import { fetchDeleteCustomer, fetchSingleCustomer } from "../../store/slices/customers";
import { useState, useEffect } from "react";
import PageLoaderWrapper from "../../components/PageLoaderWrapper";
import { ChakraTabs } from "../../components/tabs/ChakraTabs";
import { formatDate } from "@/helpers/formatDate";

export const SingleCustomerPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { currentCustomer, isLoading } = useAppSelector((state) => state.customers);
  const [isShowHistory, setShowHistory] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchSingleCustomer({ params: { id: Number(id) } }));
    }
  }, [id, dispatch]);

  // Определяем активный таб из URL
  const getActiveTabIndex = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = ["details", "projects", "applications"].indexOf(tabParam);
      if (tabIndex !== -1) {
        return tabIndex;
      }
    }
    return 0; // По умолчанию первый таб
  };

  const activeTabIndex = getActiveTabIndex();

  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных заказчика...">
        <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
          <Text color="gray.500" cursor="pointer" onClick={() => navigate("/customers")} display="flex" alignItems="center" gap="8px">
            <ArrowBackIcon />К списку заказчиков
          </Text>
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize={24} fontWeight={500}>
              Просмотр заказчика
            </Text>
            <Flex gap="16px">
              {currentCustomer?.can_edit && (
                <Button leftIcon={<Icon as={FiEdit as any} boxSize={4} />} onClick={() => navigate("edit")}>
                  Редактировать
                </Button>
              )}
              <Menu>
                <MenuButton leftIcon={<Icon as={FiClock as any} boxSize={4} />} as={Button} variant="outline">
                  История
                </MenuButton>
                <MenuList maxH="400px" overflowY="auto" w="400px">
                  {currentCustomer?.activities?.length ? (
                    currentCustomer.activities.map((activity, idx) => (
                      <Box key={idx} p="12px" borderBottom="1px solid #e2e8f0">
                        <Text fontWeight={600} fontSize="sm" marginBottom="4px">
                          {formatDate(activity.created_at, true)}
                        </Text>
                        <Text fontSize="sm" color="gray.600" whiteSpace="pre-line">
                          {activity.action}
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Box p="12px">
                      <Text fontSize="sm" color="gray.500">
                        История изменений пуста
                      </Text>
                    </Box>
                  )}
                </MenuList>
              </Menu>
              {currentCustomer?.can_remove && (
                <Button leftIcon={<Icon as={FiTrash2 as any} boxSize={4} />} colorScheme="red" onClick={() => setDeleteOpen(true)}>
                  Удалить
                </Button>
              )}
            </Flex>
          </Flex>
          {currentCustomer?.can_remove && (
            <Modal isCentered isOpen={isDeleteOpen} onClose={() => setDeleteOpen(false)}>
              <ModalOverlay backdropFilter="blur(3px)" />
              <ModalContent maxW="50vw" gap="24px" px="24px" paddingTop="32px" paddingBottom="24px">
                <ModalHeader p={0}>Подтвердите удаление</ModalHeader>
                <ModalBody>
                  <Text>
                    Вы действительно хотите удалить заказчика <b>{currentCustomer?.name}</b>?
                  </Text>
                </ModalBody>
                <ModalFooter p={0} pt="8px">
                  <Flex gap="12px">
                    <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                      Отмена
                    </Button>
                    <Button
                      colorScheme="red"
                      isLoading={isDeleting}
                      onClick={() => {
                        if (!id) return;
                        setIsDeleting(true);
                        dispatch(fetchDeleteCustomer(Number(id))).then((res: any) => {
                          setIsDeleting(false);
                          if (res.payload === 1) {
                            setDeleteOpen(false);
                            navigate("/customers");
                          }
                        });
                      }}
                    >
                      Удалить
                    </Button>
                  </Flex>
                </ModalFooter>
              </ModalContent>
            </Modal>
          )}
          <Modal isCentered isOpen={isShowHistory} onClose={() => setShowHistory(false)} size="6xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(3px)" />
            <ModalContent maxW="60vw" px="24px" paddingTop="32px" paddingBottom="24px">
              <ModalHeader p={0}>История изменений</ModalHeader>
              <ModalCloseButton />
              <ModalBody p={0} pt="8px" maxH="70vh" overflowY="auto">
                <HistoryCustomerBlock />
              </ModalBody>
            </ModalContent>
          </Modal>
          <Flex flexDirection="column" gap="32px">
            <Flex flexDirection="column" gap="16px" flex={1} minW={0}>
              <Tabs index={activeTabIndex} isLazy>
                <ChakraTabs
                  tabs={[
                    { id: "details", label: "Детали" },
                    { id: "projects", label: "Проекты" },
                    { id: "applications", label: "Заявки" },
                  ]}
                  baseUrl={`/customers/${id}`}
                />
                <TabPanels paddingTop="16px">
                  <TabPanel paddingX={0} paddingTop={0}>
                    <SingleCustomerBlock />
                  </TabPanel>
                  <TabPanel paddingX={0}>
                    <Flex flexDirection="column" gap="16px">
                      {((currentCustomer as any)?.projects ?? []).length > 0 ? (
                        (currentCustomer as any).projects.map((project: any) => (
                          <Box key={project.id} border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="16px">
                            <Flex alignItems="center" justifyContent="space-between" mb="12px">
                              <Flex alignItems="center" gap="8px">
                                <Link
                                  as={RouterLink}
                                  to={`/projects/${project.id}`}
                                  fontWeight={600}
                                  color="blue.500"
                                  _hover={{ color: "blue.600", textDecoration: "underline" }}
                                >
                                  {project.name}
                                </Link>
                              </Flex>
                              <Badge variant="subtle" colorScheme={getProjectStatusColor(project.status?.name)}>
                                {project.status?.name}
                              </Badge>
                            </Flex>
                            {project.description && (
                              <Text color="#4a5568" fontSize="14px" mb="12px">
                                {project.description}
                              </Text>
                            )}
                            <Flex flexDirection="column" gap="4px">
                              <Text fontSize="12px" color="#6B7280">
                                Ответственный аккаунт-менеджер:
                              </Text>
                              <Text fontSize="14px" color="#111827">
                                {project.responsible_user?.name ?? project.responsible_user?.username ?? ""}
                              </Text>
                            </Flex>
                          </Box>
                        ))
                      ) : (
                        <Text color="#5C657E">Проектов пока нет</Text>
                      )}
                    </Flex>
                  </TabPanel>
                  <TabPanel paddingX={0}>
                    <Flex flexDirection="column" gap="16px">
                      {(currentCustomer?.applications ?? []).map((app) => (
                        <Box key={app.id} border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="16px">
                          <Flex alignItems="center" justifyContent="space-between" mb="8px">
                            <Link
                              as={RouterLink}
                              to={`/applications/${app.id}`}
                              fontWeight={600}
                              color="blue.500"
                              _hover={{ color: "blue.600", textDecoration: "underline" }}
                            >
                              {app.name}
                            </Link>
                            <Badge variant="subtle" colorScheme={getApplicationStatusColor(app.status.name)}>
                              {app.status.name}
                            </Badge>
                          </Flex>
                          <Text color="#6B7280" fontSize="13px" mb="8px">
                            {app.project.name} • Создана: {formatDate(app.created_at)}
                          </Text>
                          {app.candidate_requirements && (
                            <Text color="#4a5568" fontSize="14px" mb="12px">
                              {app.candidate_requirements ?? "-"}
                            </Text>
                          )}

                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
                            <Flex flexDirection="column" gap="10px">
                              <Field label="Профиль" value={app.profile.name ?? "-"} />
                              <Field label="Локация" value={app.location ?? "-"} />
                              <Field label="Сколько человек нужно нанять" value={app.required_number ?? "-"} />
                              <Flex alignItems="center" gap="8px">
                                <Text fontSize="12px" color="#6B7280">
                                  Маржинальность:
                                </Text>
                                <Badge
                                  variant="subtle"
                                  colorScheme={getPriorityColor(app.marginality?.toString() ?? "")}
                                >{`${app.marginality}%`}</Badge>
                              </Flex>
                            </Flex>
                            <Flex flexDirection="column" gap="10px">
                              <Field label="Уровень" value={app.candidate_grade.map((el: any) => el.name).join(", ") ?? "-"} />
                              <Field label="Формат работы" value={app.job_format.name ?? "-"} />
                              <Field label="Занятость" value={app.attraction_period.name ?? "-"} />
                            </Flex>
                          </SimpleGrid>
                        </Box>
                      ))}
                    </Flex>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Flex>
          </Flex>
        </Flex>
      </PageLoaderWrapper>
    </Layout>
  );
};

function getProjectStatusColor(status?: string) {
  switch (status) {
    case "В работе":
      return "blue";
    case "Подбор завершен":
      return "green";
    case "Стартует":
      return "purple";
    default:
      return "gray";
  }
}

function getApplicationStatusColor(status: string) {
  switch (status) {
    case "Назначена":
      return "gray";
    case "В работе":
      return "blue";
    case "Завершена":
      return "green";
    default:
      return "gray";
  }
}

function getPriorityColor(priority: string) {
  switch (priority) {
    case "Высокий":
      return "red";
    case "Средний":
      return "yellow";
    case "Низкий":
      return "green";
    default:
      return "gray";
  }
}

function Field({ label, value, inline }: { label: string; value: string; inline?: boolean }) {
  return (
    <Flex direction={inline ? "row" : "column"} gap={inline ? "8px" : "4px"}>
      <Text fontSize="12px" color="#6B7280">
        {label}
        {inline ? ":" : ""}
      </Text>
      <Text fontSize="14px" color="#111827">
        {value || "-"}
      </Text>
    </Flex>
  );
}
